import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import * as Query from '../../api/gql/query'
import { useModuleActions } from '../../hooks/useMyPermissions'
import { ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PHOTO_ALBUMS, PHOTO_FOLDERS, REORDER_ALBUM_PHOTOS } from './api'
import PhotosPageSkeleton from './PhotosPageSkeleton'
import PhotoFolderPanel from './components/PhotoFolderPanel'
import AddToAlbumDialog from './components/AddToAlbumDialog'
import AttachToSnagDialog from './components/AttachToSnagDialog'
import SharePhotosDialog from './components/SharePhotosDialog'
import PhotoSharesSheet from './components/PhotoSharesSheet'
import AlbumBar from './components/AlbumBar'
import AlbumsView from './components/AlbumsView'
import PhotoViewer from './components/PhotoViewer'
import PhotoReportDialog from './components/PhotoReportDialog'
import PhotoSelection from './components/PhotoSelection'
import PhotoTimeline from './components/PhotoTimeline'
import PhotoDropZone from './components/PhotoDropZone'
import PhotoUploadDialog from './components/PhotoUploadDialog'
import PhotosToolbar, { PhotosView, ThumbSize } from './components/PhotosToolbar'
import { activeFilterCount, EMPTY_PHOTOS_FILTERS, PhotosFilters, toPhotoFilterInput } from './lib/filters'
import { ALL_PHOTOS, folderBreadcrumb, folderLabel, folderLabels } from './lib/folders'
import { describePhotosError, photosErrorKey } from './lib/errors'
import { photoUi } from './lib/permissions'
import { contextSelection } from './lib/bulk'
import { defectPrefill } from './lib/defect'
import SnagModal from '../snags/components/SnagModal'
import type { Photo, PhotoAlbum, PhotoFolder, PhotoShareSelection } from './types'

const SIZE_KEY = 'photos.thumbSize'

const readSize = (): ThumbSize => {
  try {
    const v = window.localStorage.getItem(SIZE_KEY)
    if (v === 'S' || v === 'M' || v === 'L') return v
  } catch (e) {
    // storage unavailable: fall back to the default
  }
  return 'M'
}

const Photos: React.FC = () => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const history = useHistory()
  const location = useLocation()
  const shareId = new URLSearchParams(location.search).get('share')
  const [shareSelection, setShareSelection] = useState<PhotoShareSelection | null>(null)
  const [sharesOpen, setSharesOpen] = useState(false)
  const projectId: string | undefined = meData?.me?.currentProject?.id
  const { canView, canCreate, canEdit, canDelete, canExport, canAdmin, loading: rightsLoading } = useModuleActions('PHOTOS', projectId)
  const { canCreate: canCreateDefect, canEdit: canEditDefect } = useModuleActions('ISSUES', projectId)
  const [attachIds, setAttachIds] = useState<string[] | null>(null)
  const [defectIds, setDefectIds] = useState<string[] | null>(null)
  const [view, setView] = useState<PhotosView>('timeline')
  const [filters, setFilters] = useState<PhotosFilters>(EMPTY_PHOTOS_FILTERS)
  const [size, setSize] = useState<ThumbSize>(readSize)
  const [viewer, setViewer] = useState<{ photoId: string; orderedIds: string[] } | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loadedPhotos, setLoadedPhotos] = useState<Photo[]>([])
  // Scope state: the folder panel drives folderId/includeSubfolders; the album tab drives albumId.
  const [scope, setScope] = useState<{ folderId: string | null; includeSubfolders: boolean; albumId: string | null }>({
    folderId: null,
    includeSubfolders: false,
    albumId: null,
  })
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [albumTargetIds, setAlbumTargetIds] = useState<string[] | null>(null)
  // null = closed; the files come from a drop onto the photo area, [] from the Upload button.
  const [uploadFiles, setUploadFiles] = useState<File[] | null>(null)
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null)
  // A cleared selection takes its right-click menu with it, or the next selection would reopen it.
  useEffect(() => {
    if (!selectedIds.size) setMenuAt(null)
  }, [selectedIds])
  const defect = useMemo(() => {
    if (!defectIds) return null
    const byId = new Map(loadedPhotos.map((p) => [p.id, p]))
    return defectPrefill(defectIds.map((id) => byId.get(id)).filter((p): p is Photo => !!p))
  }, [defectIds, loadedPhotos])
  const filter = useMemo(() => toPhotoFilterInput(filters, { ...scope, shareId }), [filters, scope, shareId])
  const { data: folderData, loading: foldersLoading } = useQuery<{ photoFolders: PhotoFolder[] }>(PHOTO_FOLDERS, {
    variables: { projectId },
    skip: !projectId || !canView,
  })
  const { data: albumData } = useQuery<{ photoAlbums: PhotoAlbum[] }>(PHOTO_ALBUMS, {
    variables: { projectId },
    skip: !projectId || !canView || !scope.albumId,
  })
  const [reorderAlbum, { loading: reordering }] = useMutation(REORDER_ALBUM_PHOTOS, {
    context: { handlesErrors: true },
    refetchQueries: ['photos', 'photoAlbums'],
  })

  if (!projectId || rightsLoading || foldersLoading) return <PhotosPageSkeleton />
  if (!canView) return <div className="p-8 text-center text-muted-foreground">{t('Photos_no_access')}</div>

  const openDefect = canCreateDefect ? setDefectIds : undefined
  const ui = photoUi(meData.me.id, meData.me.rights, { canCreate, canEdit, canDelete, canExport, canAdmin })

  const breadcrumb = scope.folderId && !scope.albumId ? folderBreadcrumb(folderData?.photoFolders ?? [], scope.folderId) : []

  const album = (albumData?.photoAlbums ?? []).find((a) => a.id === scope.albumId)
  // A filtered album shows a subset; reordering it would push the hidden photos to the end.
  const unfiltered = activeFilterCount(filters) === 0 && !filters.search.trim() && !filters.buildingId && !filters.floorId

  const changeView = (next: PhotosView) => {
    setView(next)
    if (next === 'timeline' && scope.albumId) setScope({ ...scope, albumId: null })
    setSelectedIds(new Set())
  }

  const reorder = async (photoIds: string[]) => {
    if (!scope.albumId || reordering) return
    try {
      await reorderAlbum({ variables: { albumId: scope.albumId, photoIds } })
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  const leaveShare = () => {
    const params = new URLSearchParams(location.search)
    params.delete('share')
    const search = params.toString()
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' })
  }

  const changeSize = (next: ThumbSize) => {
    setSize(next)
    try {
      window.localStorage.setItem(SIZE_KEY, next)
    } catch (e) {
      // storage unavailable: the choice just won't persist
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
      <aside>
        <PhotoFolderPanel
          projectId={projectId}
          selectedFolderId={scope.albumId ? ALL_PHOTOS : scope.folderId ?? ALL_PHOTOS}
          onSelect={(id) => {
            setScope({ ...scope, folderId: id === ALL_PHOTOS ? null : id, albumId: null })
            setView('timeline')
            setSelectedIds(new Set())
          }}
          includeSubfolders={scope.includeSubfolders}
          onIncludeSubfoldersChange={(includeSubfolders) => setScope({ ...scope, includeSubfolders })}
          ui={ui}
          onPhotosMoved={() => setSelectedIds(new Set())}
          onShareFolder={(folderId) => setShareSelection({ folderId, includeSubfolders: scope.includeSubfolders })}
        />
      </aside>
      <section className="min-w-0">
        {selectedIds.size > 0 ? (
          <PhotoSelection
            projectId={projectId}
            selectedIds={Array.from(selectedIds)}
            photosById={new Map(loadedPhotos.map((p) => [p.id, p]))}
            ui={ui}
            onClear={() => setSelectedIds(new Set())}
            onAddToAlbum={ui.canManageStructure ? setAlbumTargetIds : undefined}
            onReport={() => setReportOpen(true)}
            onCreateDefect={openDefect}
            onAttachToSnag={canEditDefect ? setAttachIds : undefined}
            onShare={(ids) => setShareSelection({ photoIds: ids })}
            menuAt={menuAt}
            onMenuClose={() => setMenuAt(null)}
          />
        ) : (
          <PhotosToolbar
            projectId={projectId}
            view={view}
            onViewChange={changeView}
            filters={filters}
            onFiltersChange={(f) => { setFilters(f); setSelectedIds(new Set()) }}
            size={size}
            onSizeChange={changeSize}
            ui={ui}
            onUpload={ui.canUpload ? () => setUploadFiles([]) : undefined}
            onReport={view === 'timeline' || scope.albumId ? () => setReportOpen(true) : undefined}
            onShowShares={() => setSharesOpen(true)}
          />
        )}
        {shareId && (
          <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
            <span className="min-w-0 flex-1">{t('Photos_share_landing')}</span>
            <Button variant="outline" size="sm" className="h-8" onClick={leaveShare}>{t('Photos_share_show_all')}</Button>
          </div>
        )}
        {breadcrumb.length > 0 && (
          <nav aria-label={t('Photos_folders')} className="flex flex-wrap items-center gap-1 pb-2 text-sm text-muted-foreground">
            {breadcrumb.map((f, i) => (
              <React.Fragment key={f.id}>
                {i > 0 && <ChevronRight className="h-4 w-4" />}
                <span className={i === breadcrumb.length - 1 ? 'font-medium text-foreground' : undefined}>{folderLabel(f, folderLabels(t))}</span>
              </React.Fragment>
            ))}
          </nav>
        )}
        <PhotoDropZone enabled={ui.canUpload} onFiles={setUploadFiles}>
          {view === 'albums' && !scope.albumId ? (
            <AlbumsView
              projectId={projectId}
              ui={ui}
              onOpenAlbum={(albumId) => {
                setScope({ folderId: null, includeSubfolders: false, albumId })
                setSelectedIds(new Set())
              }}
            />
          ) : (
            <>
              {scope.albumId && (
                <AlbumBar
                  album={album}
                  ui={ui}
                  selectedIds={Array.from(selectedIds)}
                  reordering={reordering}
                  onBack={() => {
                    setScope({ ...scope, albumId: null })
                    setSelectedIds(new Set())
                  }}
                  onClearSelection={() => setSelectedIds(new Set())}
                  onShareAlbum={(albumId) => setShareSelection({ albumId })}
                />
              )}
              <PhotoTimeline
                projectId={projectId}
                filter={filter}
                size={size}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onPhotosChange={setLoadedPhotos}
                onTotalChange={setTotalCount}
                onOpen={(photoId, orderedIds) => setViewer({ photoId, orderedIds })}
                onReorder={scope.albumId && album?.canEdit && unfiltered && !reordering ? reorder : undefined}
                onPhotoContextMenu={(photoId, e) => {
                  e.preventDefault()
                  setSelectedIds((cur) => contextSelection(cur, photoId))
                  setMenuAt({ x: e.clientX, y: e.clientY })
                }}
              />
            </>
          )}
        </PhotoDropZone>
      </section>
      {ui.canUpload && (
        <PhotoUploadDialog
          projectId={projectId}
          open={!!uploadFiles}
          onOpenChange={(o) => !o && setUploadFiles(null)}
          defaultFolderId={scope.folderId}
          ui={ui}
          initialFiles={uploadFiles ?? []}
        />
      )}
      <PhotoReportDialog
        projectId={projectId}
        open={reportOpen}
        onOpenChange={setReportOpen}
        selectedIds={Array.from(selectedIds)}
        filter={filter}
        matchCount={totalCount}
      />
      <AddToAlbumDialog
        projectId={projectId}
        photoIds={albumTargetIds ?? []}
        open={!!albumTargetIds}
        onOpenChange={(o) => !o && setAlbumTargetIds(null)}
        onDone={() => setSelectedIds(new Set())}
      />
      <PhotoViewer
        projectId={projectId}
        photoId={viewer?.photoId ?? null}
        orderedIds={viewer?.orderedIds ?? []}
        onNavigate={(photoId) => setViewer((v) => (v ? { ...v, photoId } : v))}
        onClose={() => setViewer(null)}
        ui={ui}
        onCreateDefect={openDefect}
        onAttachToSnag={canEditDefect ? setAttachIds : undefined}
        onShare={(ids) => setShareSelection({ photoIds: ids })}
      />
      <SharePhotosDialog projectId={projectId} selection={shareSelection} onOpenChange={(o) => !o && setShareSelection(null)} ui={ui} />
      <PhotoSharesSheet projectId={projectId} open={sharesOpen} onOpenChange={setSharesOpen} />
      <AttachToSnagDialog
        projectId={projectId}
        photoIds={attachIds ?? []}
        open={!!attachIds}
        onOpenChange={(o) => !o && setAttachIds(null)}
        onDone={() => setSelectedIds(new Set())}
      />
      {defectIds && defect && (
        <SnagModal
          isOpen
          photoIds={defectIds}
          photoThumbs={defect.thumbs}
          floorId={defect.floorId}
          initialTrade={defect.trade}
          onClose={(saved) => {
            setDefectIds(null)
            if (saved) setSelectedIds(new Set())
          }}
        />
      )}
    </div>
  )
}

export default Photos
